import type { CardPrintTemplate, DistrictMode } from '@citizen-walk/shared-types';
import type { CardPrintField } from './cardLayout';
import { sortFields } from './cardLayout';
import {
  BUILTIN_CARD_FIELDS,
  BUILTIN_EXTRA_FIELD_PATHS,
  buildDefaultJobFields,
  discoverAiFields,
} from './builtinFields';
import type { CardPrintPost } from './useCardPrintPosts';

/**
 * AW6 — 印刷ジョブ単位の設定 (master template を変えずにこの印刷ジョブだけで効く設定)。
 *
 * old2 `types.ts::CardPrintConfig` を v2 に移植:
 *   - fields: 表示項目 (順序付き) — master template の fields をベースに add/remove/reorder
 *   - adminNote: 全カード共通の追記テキスト
 *   - startIndex: 開始セル (0..9、使い残しラベル台紙対応)
 *   - cropMarks: カットライン (none / corners / lines)
 *   - fillBehavior: 投稿数 < 10 のときの埋め方 (空セル / 繰り返し)
 *   - zoom: preview ズーム (0=自動 / 0.5 / 0.75 / 1)
 *
 * theme は別 state (ResolvedCardTheme) として CardPrintPage が保持する。
 */

export type CropMarks = 'none' | 'corners' | 'lines';
export type FillBehavior = 'leave-empty' | 'repeat-posts';

/** 表示項目どうしの行間 (mm) の既定値。preview / PDF 共通。 */
export const DEFAULT_FIELD_GAP_MM = 0.6;

/** QR コードの一辺 (mm) の既定値・受理範囲。preview / PDF 共通。 */
export const DEFAULT_QR_SIZE_MM = 8;
export const MIN_QR_SIZE_MM = 8;
/** カード (55×91mm) 右下に収まる上限。これ以上はテキストを大きく覆うため上限を設ける。 */
export const MAX_QR_SIZE_MM = 20;

/** qrSize_mm を受理範囲 (MIN..MAX) にクランプ。非数値は既定。 */
export function clampQrSize(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_QR_SIZE_MM;
  return Math.max(MIN_QR_SIZE_MM, Math.min(MAX_QR_SIZE_MM, value));
}

export interface CardPrintJobConfig {
  fields: CardPrintField[];
  adminNote: string;
  startIndex: number;
  cropMarks: CropMarks;
  fillBehavior: FillBehavior;
  /** 表示項目どうしの行間 (mm)。小さくすると 1 枚により多くの項目を載せられる。既定 0.6。 */
  fieldGap_mm: number;
  /**
   * QR コードの一辺 (mm)。大きくするとインクジェットのにじみでも読み取りやすくなる。
   * 既定 8 (MIN..MAX_QR_SIZE_MM)。表示項目に「QRコード(地図リンク)」を含むときに有効。
   */
  qrSize_mm: number;
  /** 0 = 自動フィット (ResizeObserver)、それ以外は固定倍率。 */
  zoom: number;
}

/** 管理者追記の特殊 field key (source='post' 等とは別扱いで adminNote テキストを描画)。 */
export const ADMIN_NOTE_FIELD_KEY = '__adminNote';

export function makeAdminNoteField(): CardPrintField {
  return {
    key: ADMIN_NOTE_FIELD_KEY,
    label: '管理者追記',
    source: 'post',
    fieldPath: '',
    displayOrder: 9999,
  };
}

/** field が管理者追記擬似 field か。 */
export function isAdminNoteField(field: Pick<CardPrintField, 'key'>): boolean {
  return field.key === ADMIN_NOTE_FIELD_KEY;
}

/**
 * 「ジョブの初期 fields」を構築。
 *
 * - template (fields あり) → template.fields を displayOrder 昇順に並べたもの。
 * - template 無し / fields 空 → 地区 mode の組み込み既定 (buildDefaultJobFields)。
 *   `mode` 未指定なら空配列 (テンプレ駆動の従来挙動と後方互換)。
 *
 * テンプレートが無くてもカード印刷できるようにするための入口
 * (mode を渡すと old2 `DEFAULT_FIELDS_BY_MODE` 相当の初期項目が出る)。
 */
export function buildInitialJobFields(
  template: Pick<CardPrintTemplate, 'fields'> | null,
  mode?: DistrictMode | null,
): CardPrintField[] {
  if (template && template.fields.length > 0) {
    return sortFields(template.fields).map((f) => ({ ...f }));
  }
  if (mode !== undefined) return buildDefaultJobFields(mode);
  return [];
}

/**
 * 投稿群から発見した extraFields キーを `source='extraFields'` の field 候補に変換。
 * 旧 `CardLayoutSettingsPanel` の `extra:${key}` 追加候補に相当。
 *
 * `labelMap` (データ設定 postFields の key→日本語名) があればラベルにその日本語名を採用し、
 * 無いキーは `カスタム: <key>` にフォールバック。
 */
export function discoverExtraFields(
  posts: readonly CardPrintPost[],
  labelMap?: Record<string, string>,
  skipKeys?: ReadonlySet<string>,
): CardPrintField[] {
  const keys = new Set<string>();
  for (const p of posts) {
    for (const k of Object.keys(p.extraFields ?? {})) {
      if (skipKeys?.has(k)) continue; // 組み込みカタログが既にカバーするキーは二重に出さない
      keys.add(k);
    }
  }
  return [...keys].sort().map((k) => ({
    key: `extra:${k}`,
    label: labelMap?.[k] ?? `カスタム: ${k}`,
    source: 'extraFields' as const,
    fieldPath: k,
    displayOrder: 0,
  }));
}

/**
 * 追加候補プール (jobFields 未含のもの) を構築。
 *
 * 「テンプレートがなくても自由にデータを持ってくる」ため、以下を常に候補に含める:
 *   - base template fields (あれば、先頭優先)
 *   - 組み込みカタログ (title / category / comment / author / createdAt / location /
 *     agreeCount / aiSummary / aiTopCategory / aiEmotion / qrCode)
 *   - 投稿から発見した extraFields (地区固有データ)
 *   - 投稿の aiAnalysis から発見した AI フィールド (ecw / generic / bousai / walker)
 *   - 管理者追記 (擬似 field)
 * key 重複は先に push したものを優先し dedupe。
 */
export function buildAddableFields(
  template: Pick<CardPrintTemplate, 'fields'> | null,
  posts: readonly CardPrintPost[],
  jobFields: readonly CardPrintField[],
  labelMap?: Record<string, string>,
): CardPrintField[] {
  const used = new Set(jobFields.map((f) => f.key));
  const seen = new Set<string>();
  const pool: CardPrintField[] = [];

  function push(f: CardPrintField): void {
    if (used.has(f.key) || seen.has(f.key)) return;
    seen.add(f.key);
    pool.push(f);
  }

  if (template) {
    for (const f of sortFields(template.fields)) push({ ...f });
  }
  for (const f of BUILTIN_CARD_FIELDS) push({ ...f });
  // 組み込みがプレーンテキストでカバーする extraFields (aiGeneratedTitle 等) は discovery から除外し二重を防ぐ。
  for (const f of discoverExtraFields(posts, labelMap, BUILTIN_EXTRA_FIELD_PATHS)) push(f);
  for (const f of discoverAiFields(posts)) push(f);
  push(makeAdminNoteField());

  return pool;
}

export function defaultJobConfig(): CardPrintJobConfig {
  return {
    fields: [],
    adminNote: '',
    startIndex: 0,
    cropMarks: 'none',
    fillBehavior: 'leave-empty',
    fieldGap_mm: DEFAULT_FIELD_GAP_MM,
    qrSize_mm: DEFAULT_QR_SIZE_MM,
    zoom: 0,
  };
}

/**
 * fillBehavior に応じて選択投稿の描画キューを構築。
 * 旧 buildCardPdf の repeat-posts ロジックを pure helper 化 (preview / PDF 共通)。
 */
export function buildPostQueue<T>(
  posts: readonly T[],
  fillBehavior: FillBehavior,
  perSheet: number,
): T[] {
  if (fillBehavior !== 'repeat-posts' || posts.length === 0 || posts.length >= perSheet) {
    return [...posts];
  }
  let queue: T[] = [];
  while (queue.length < perSheet) queue = queue.concat(posts);
  return queue;
}
